// Copyright © Sun West Mortgage Company, Inc. All rights reserved.
import React, { useState, useRef, useEffect } from "react";
import htmlParser from "html-react-parser";
import "./index.css";

export default function Body({ data }) {
  const domain = process.env.REACT_APP_DOMAIN || "swmc";
  const domainData = data?.[domain] || {};
  const registeredTrademarks = domainData.registered || [];
  const pendingTrademarks = domainData.pending || [];
  const CDN_PATH = process.env.REACT_APP_CDN_PATH || "";

  const TrademarkCard = ({ trademark, folder }) => {
    const hasText = trademark.logo && trademark.logo.includes("text-logo.webp");
    const hasIntl =
      trademark.internationalRegistrations &&
      trademark.internationalRegistrations.length > 0;
    const [intlOpen, setIntlOpen] = useState(false);
    const cardRef = useRef(null);

    // Close on outside click
    useEffect(() => {
      if (!intlOpen) return;
      const handleClick = (e) => {
        if (cardRef.current && !cardRef.current.contains(e.target)) {
          setIntlOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }, [intlOpen]);

    return (
      <div
        className={`trademark-card${intlOpen ? " trademark-card--expanded" : ""}`}
        ref={cardRef}
      >
        {/* ── Main face ── */}
        <div className="trademark-card-inner">
          <div className="logo-section">
            <img
              src={`${CDN_PATH}${folder}/${trademark.logo}`}
              alt={trademark.wordmark || "Trademark logo"}
              className="trademark-logo"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>

          <div className="info-section">
            <div className="description-div">
              {trademark.wordmark && (
                <p className="wordmark">
                  <span className="label">Wordmark:</span> {trademark.wordmark}
                </p>
              )}
              <p className="serial">
                <span className="label">U.S. Serial Number:</span>{" "}
                {trademark.serialNumber}
              </p>
              <p className="mark-type">
                <span className="label">Mark Type:</span> {trademark.markType}
              </p>
              {trademark.trademarkSymbol && (
                <p className="trademark-symbol">
                  <span className="label">Trademark Symbol:</span>{" "}
                  <span className="symbol-value">
                    {htmlParser(trademark.trademarkSymbol)}
                  </span>
                </p>
              )}
            </div>

            {/* Footer: intl button (optional) + logo/text badge */}
            <div className={`card-footer${hasIntl ? "" : " card-footer--right"}`}>
              {hasIntl && (
                <button
                  className="intl-btn"
                  onClick={() => setIntlOpen(true)}
                  aria-expanded={intlOpen}
                >
                  <span className="intl-btn__text">International Registrations</span>
                  <svg className="intl-btn__arrow" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                </button>
              )}
              <button
                className={`action-button ${hasText ? "text-button" : "logo-button"}`}
              >
                {hasText ? "Text" : "Logo"}
              </button>
            </div>
          </div>
        </div>

        {/* ── International Registrations overlay panel (inside the card) ── */}
        {hasIntl && (
          <div
            className={`intl-panel${intlOpen ? " intl-panel--open" : ""}`}
            aria-hidden={!intlOpen}
          >
            <div className="intl-panel__inner">
              <div className="intl-panel__header">
                <button
                  className="intl-panel__close"
                  onClick={() => setIntlOpen(false)}
                  aria-label="Close"
                >
                  &#9664;
                </button>
                <h4 className="intl-panel__heading">International Registrations</h4>
              </div>
              <div className="intl-panel__body">
                <div
                  className="intl-panel__scroll"
                  ref={(el) => {
                    if (el) {
                      const track = el.nextSibling;
                      if (track) {
                        track.style.display = el.scrollHeight > el.clientHeight ? '' : 'none';
                      }
                    }
                  }}
                  onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target;
                    const track = e.target.nextSibling;
                    const thumb = track?.firstChild;
                    if (thumb) {
                      const ratio = scrollTop / (scrollHeight - clientHeight);
                      const trackHeight = track.clientHeight;
                      const thumbHeight = thumb.clientHeight;
                      thumb.style.transform = `translateY(${ratio * (trackHeight - thumbHeight)}px)`;
                    }
                  }}
                >
                  <ul className="intl-list">
                    {trademark.internationalRegistrations.map((entry, idx) => (
                      <li key={idx} className="intl-list__item">
                        {entry.country} ({entry.status}): {entry.registrationNumber}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="intl-panel__scrollbar-track">
                  <div className="intl-panel__scrollbar-thumb" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="trademarks-body">
      <div className="trademarks-container">
        {registeredTrademarks.length > 0 && (
          <div className="trademark-section">
            <div className="section-heading-wrapper">
              <h2 className="section-heading">
                <span className="heading-line"></span>
                <span className="heading-text">Registered Trademarks</span>
                <span className="heading-line"></span>
              </h2>
            </div>
            <div className="trademarks-grid">
              {registeredTrademarks.map((trademark) => (
                <TrademarkCard
                  key={trademark.id}
                  trademark={trademark}
                  folder={domain}
                />
              ))}
            </div>
          </div>
        )}

        {pendingTrademarks.length > 0 && (
          <div className="trademark-section">
            <div className="section-heading-wrapper">
              <h2 className="section-heading">
                <span className="heading-line"></span>
                <span className="heading-text">Pending Trademarks</span>
                <span className="heading-line"></span>
              </h2>
            </div>
            <div className="trademarks-grid">
              {pendingTrademarks.map((trademark) => (
                <TrademarkCard
                  key={trademark.id}
                  trademark={trademark}
                  folder={domain}
                />
              ))}
            </div>
          </div>
        )}

        {registeredTrademarks.length === 0 && pendingTrademarks.length === 0 && (
          <div className="no-trademarks">
            No trademarks available to display.
          </div>
        )}
      </div>
    </section>
  );
}